"use client";

import { useTranslation } from "react-i18next";
import { usePathname } from "next/navigation";
import { getLocaleFromPath } from "../lib/i18n/utils";

type BookingCTAProps = {
  className?: string;
  variant?: "default" | "mobile";
};

/**
 * BookingCTA Component
 * 
 * Reusable CTA component for booking/reservation links.
 * 
 * Features:
 * - Automatically detects locale from pathname and passes to WebHotelier
 * - Sets currency to EUR for Greece
 * - Opens in new tab with proper security attributes
 * 
 * Based on WebHotelier documentation:
 * https://docs.webhotelier.net/integration-site-start
 * 
 * SEO Rules:
 * - No `rel="nofollow"` (follow links for SEO)
 * 
 * Security:
 * - External links open in new tab with `target="_blank"`
 * - Includes `rel="noopener noreferrer"` for security
 * 
 * @param className - Additional CSS classes
 * @param variant - Button variant: "default" (desktop) or "mobile"
 */
const BOOKING_URL_BASE = process.env.NEXT_PUBLIC_BOOKING_URL || "https://tospititissofias.reserve-online.net/";

/**
 * Build booking URL with WebHotelier parameters
 * 
 * Parameters:
 * - lang: Language/locale (e.g., 'el', 'en') - automatically matches site language
 * - currency: Currency code (EUR for Greece)
 */
function buildBookingUrl(locale: string): string {
  const url = new URL(BOOKING_URL_BASE);
  
  // Map locale to WebHotelier language codes
  // WebHotelier supports ISO 639-1 language codes
  const languageMap: Record<string, string> = {
    'el': 'el', // Greek
    'en': 'en', // English
  };
  
  const language = languageMap[locale] || 'el'; // Default to Greek
  
  // Language parameter - ensures booking engine opens in user's language
  url.searchParams.set('lang', language);
  
  // Currency parameter - Greece uses Euro
  url.searchParams.set('currency', 'EUR');
  
  return url.toString();
}

export default function BookingCTA({ className, variant = "default" }: BookingCTAProps) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const locale = getLocaleFromPath(pathname);
  const bookingUrl = buildBookingUrl(locale);
  
  const baseClassName = variant === "mobile" ? "primary-button-mobile" : "primary-button";
  const combinedClassName = className ? `${baseClassName} ${className}` : baseClassName;

  return (
    <a
      href={bookingUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={combinedClassName}
    >
      <span suppressHydrationWarning>{t("Make a reservation")}</span>
    </a>
  );
}

